/**
 * Z-Score HeatMap Component
 * Displays heatmap of z-scores by department/cohort
 * Uses Recharts for visualization
 */

import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { TrendingUp } from 'lucide-react';

const ZScoreHeatMap = ({ heatmapData }) => {
  if (!heatmapData || heatmapData.length === 0) {
    return (
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <TrendingUp size={20} style={{ marginRight: 8 }} />
            Z-Score Distribution
          </Typography>
          <Typography color="text.secondary">No data available</Typography>
        </CardContent>
      </Card>
    );
  }

  // Transform data for scatter plot
  const chartData = heatmapData.map((item) => ({
    department: item.department || 'Unknown',
    groupZScore: parseFloat(item.avg_group_z_score) || 0,
    appZScore: parseFloat(item.avg_app_z_score) || 0,
    userCount: parseInt(item.user_count) || 1
  }));

  // Color scale based on z-score magnitude
  const getColor = (zScore) => {
    const absZScore = Math.abs(zScore);
    if (absZScore >= 2.5) return '#dc3545'; // Critical (red)
    if (absZScore >= 1.5) return '#fd7e14'; // High (orange)
    if (absZScore >= 0.8) return '#ffc107'; // Moderate (yellow)
    return '#28a745'; // Acceptable (green)
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
            {data.department}
          </p>
          <p style={{ margin: '4px 0', fontSize: '0.9rem' }}>
            Group Z-Score: <strong>{data.groupZScore.toFixed(2)}</strong>
          </p>
          <p style={{ margin: '4px 0', fontSize: '0.9rem' }}>
            App Z-Score: <strong>{data.appZScore.toFixed(2)}</strong>
          </p>
          <p style={{ margin: '4px 0', fontSize: '0.9rem' }}>
            Users: <strong>{data.userCount}</strong>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <TrendingUp size={20} style={{ marginRight: 8 }} />
          Z-Score Distribution by Department
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: '0.9rem', mb: 2 }}>
          Bubble size represents number of users. Color indicates risk level.
        </Typography>

        <ResponsiveContainer width="100%" height={400}>
          <ScatterChart margin={{ top: 20, right: 30, bottom: 60, left: 60 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              dataKey="groupZScore"
              name="Group Z-Score"
              label={{ value: 'Group Z-Score', position: 'insideBottom', offset: -10 }}
              domain={['auto', 'auto']}
            />
            <YAxis
              type="number"
              dataKey="appZScore"
              name="App Z-Score"
              label={{ value: 'App Z-Score', angle: -90, position: 'insideLeft' }}
              domain={['auto', 'auto']}
            />
            <ZAxis
              type="number"
              dataKey="userCount"
              range={[100, 1000]}
              name="User Count"
            />
            <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              payload={[
                { value: 'Critical (|z| >= 2.5)', type: 'circle', color: '#dc3545' },
                { value: 'High (|z| >= 1.5)', type: 'circle', color: '#fd7e14' },
                { value: 'Moderate (|z| >= 0.8)', type: 'circle', color: '#ffc107' },
                { value: 'Acceptable (|z| < 0.8)', type: 'circle', color: '#28a745' }
              ]}
            />
            <Scatter data={chartData}>
              {chartData.map((entry, index) => {
                const avgZScore = (Math.abs(entry.groupZScore) + Math.abs(entry.appZScore)) / 2;
                return <Cell key={`cell-${index}`} fill={getColor(avgZScore)} />;
              })}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default ZScoreHeatMap;
